import { useQuery } from 'react-query';
import { Client } from 'fulcrum-app';
import _ from 'lodash';
import { Form } from 'fulcrum-core';
import { graphConfig } from '../authConfig';
import { v4 as id } from "uuid";
import { OBSERVATIONS_KEY } from './formObservationLayout';

// setup the fulcrum client
// eslint-disable-next-line no-undef
const client = new Client(process.env.REACT_APP_FULCRUM);

const getForm = async (formID) => {
  return formID ? client.forms.find(formID) : null
}

const useForm = (formID) => {
  return useQuery(['form', formID], () => getForm(formID), {})
}

const getClassificationSet = async (classificationID) => {
  return classificationID ? client.classificationSets.find(classificationID) : null;
}

const useClassificationSet = (classificationID) => {
  return useQuery(['classification', classificationID], () => getClassificationSet(classificationID), {})
}

const getChoiceList = async (choice_list_id) => {
  return choice_list_id ? client.choiceLists.find(choice_list_id) : null;
}

const useChoiceList = (choice_list_id) => {
  return useQuery(['choiceList', choice_list_id], () => getChoiceList(choice_list_id), {})
}

//===============================================================================================
// a function to get all apps in fulcrum with this string in the name - todo - a better way to get exactly which apps should be listed and possibly pass in
// the logged in user and only return a subset of records based on what they should be able to access
const getApps = async () => {
  const { rows } = await client.query(`SELECT name, form_id FROM tables WHERE LOWER(name) LIKE '%compliance audit%' AND type = 'form' ORDER BY name`);
  return rows;
};

function useApps() {
  return useQuery(['auditApps'], () => getApps(), {});
}

//===============================================================================================
// get the audit records from the selected parent app
const getAudits = async (appid) => {
  if (appid) {
    const { rows } = await client.query(`SELECT * FROM "${appid}" ORDER BY _title`);
    return rows;
  }
  return null;
};

function useAudits(appid) {
  return useQuery(['audits', appid], () => getAudits(appid), {});
}
//===============================================================================================

//===============================================================================================
// get the observation records from the selected audit record (flat) or site visit (nested)
const getObservations = async (appid, recordID, siteVisitRowId, isNested) => {
  if (!appid || !recordID) {
    return null;
  }
  if (isNested) {
    if (!siteVisitRowId) {
      return null;
    }
    const { rows } = await client.query(
      `SELECT * FROM "${appid}/${OBSERVATIONS_KEY}" WHERE _record_id = '${recordID}' AND _parent_id = '${siteVisitRowId}' ORDER BY loc_id`
    );
    return rows;
  }
  const { rows } = await client.query(
    `SELECT * FROM "${appid}/${OBSERVATIONS_KEY}" WHERE _record_id = '${recordID}' ORDER BY loc_id`
  );
  return rows;
};

function useObservations(appid, recordID, siteVisitRowId, isNested = false) {
  const nested = Boolean(isNested);
  return useQuery(
    ['observations', appid, recordID, siteVisitRowId, nested],
    () => getObservations(appid, recordID, siteVisitRowId, nested),
    { enabled: Boolean(appid && recordID && (!nested || siteVisitRowId)) }
  );
}
//===============================================================================================

//===============================================================================================
// get the parent records from selected fulcrum app
const getParentRecords = async (appid) => {
  if (appid) {
    const { rows } = await client.query(`SELECT * FROM "${appid}"`);
    return rows;
  }
  return null;
};

function useParentRecords(appid) {
  return useQuery(['parentRecords', appid], () => getParentRecords(appid), {});
}
//===============================================================================================

//===============================================================================================
// get child records from selected repeatable
const getChildRecords = async (appid, recordID, key) => {
  if (appid && recordID && key) {
    const { rows } = await client.query(`SELECT * FROM "${appid}/${key}" WHERE _parent_id = '${recordID}'`);
    return rows;
  }
  return null;
};

function useChildRecords(appid, recordID, key) {
  return useQuery(
    ['childRecords', appid, recordID, key],
    () => getChildRecords(appid, recordID, key),
    { enabled: Boolean(appid && recordID && key) }
  );
}
//===============================================================================================

// get form definition from fulcrum API

async function getFormFields(appid) {
  const rawForm = await client.forms.find(appid);
  const form = new Form(rawForm);
  const formSchema = Object.entries(form.elementsByDataName).map((entry) => ({
    [entry[0]]: entry[1].key
  }));
  return formSchema.reduce((r, c) => Object.assign(r, c), {});
}

//===============================================================================================
// add observation record
const addObservation = async (appid, recordID, options = {}) => {
  const { isNested = false, siteVisitRowId = null, parentRepeatableKey = null } = options;
  try {
    const formFields = await getFormFields(appid).catch(error => {
      console.log(error)
      return { title: 'Saved', msg: error, type: 'error' }
    });

    const record = await getRecord(recordID).catch(error => {
      console.log(error)
      return { title: 'Saved', msg: error, type: 'error' }
    });

    let form_values = {}
    Object.values(formFields).forEach(key => {
      if (key === 'b099') {
        form_values[key] = 'New Record'
      }
    })
    let row = {
      id: id(),
      form_values: form_values
    }
    if (isNested) {
      if (!siteVisitRowId || !parentRepeatableKey) {
        return { title: 'Error', msg: 'Missing site visit for nested observations.', type: 'error' };
      }
      const siteVisit = record.form_values[parentRepeatableKey]?.find((sv) => sv.id === siteVisitRowId);
      if (!siteVisit) {
        return { title: 'Error', msg: 'Site visit row not found on record.', type: 'error' };
      }
      if (!siteVisit.form_values) {
        siteVisit.form_values = {};
      }
      if (!siteVisit.form_values[OBSERVATIONS_KEY]) {
        siteVisit.form_values[OBSERVATIONS_KEY] = [];
      }
      siteVisit.form_values[OBSERVATIONS_KEY].unshift(row);
    } else {
      record.form_values[OBSERVATIONS_KEY].unshift(row);
    }
    await client.records.update(recordID, record).catch(error => {
      console.log(error)
      return { title: 'Saved', msg: error, type: 'error' }
    });

    return { title: 'Saved', msg: 'Saved Successfully!', type: 'success' };
  } catch (error) {
    console.log(error)
    return { title: 'Error', msg: `Ooops, an error occured while adding new observation. ${error}`, type: 'error' };
  }
}
//===============================================================================================
// mutate observations (currently limited by appid)
const updateObservations = async (appid, recordID, changes, options = {}) => {
  const { isNested = false, siteVisitRowId = null, parentRepeatableKey = null } = options;
  try {
    const formFields = await getFormFields(appid);
    const record = await getRecord(recordID);

    if (record?.status === 'Complete') {
      return { title: 'Warning', msg: 'Sorry the record status is already marked as Complete in Fulcrum. Changes not saved.', type: 'warning' };
    } else {
      // integrate query API with REST api structure
      // switch data names from query api out for the corresponding key that comes with a record json object
      const newData = changes.map((row) => {
        delete Object.assign(row, { [formFields[row.col]]: row.changeValue })[row.col];
        delete row.col;
        delete row.data_name;
        delete row.value;

        // remove null object entries 
        const removeNulls = _.flow([
          Object.entries,
          (arr) => arr.filter(([, value]) => value !== null),
          Object.fromEntries
        ])(row);

        return {
          form_values: removeNulls
        };
      });

      let observationRows;
      if (isNested) {
        if (!siteVisitRowId || !parentRepeatableKey) {
          return { title: 'Error', msg: 'Missing site visit for nested observations.', type: 'error' };
        }
        const siteVisit = record.form_values[parentRepeatableKey]?.find((sv) => sv.id === siteVisitRowId);
        observationRows = siteVisit?.form_values?.[OBSERVATIONS_KEY];
      } else {
        observationRows = record.form_values[OBSERVATIONS_KEY];
      }
      if (!observationRows) {
        return { title: 'Error', msg: 'Observations not found on record.', type: 'error' };
      }

      // loop over all our changes and merge our changes with server record data - this mutates the original record we just got from the server
      newData.forEach((change) => {
        _.merge(observationRows.find((r) => r.id === change.form_values._child_record_id), change);
      });

      // console.log('updated', record)
      // push the mutated record back to the server
      await client.records.update(recordID, record).catch((error) => console.log(error));

      return { title: 'Saved', msg: 'Saved Successfully!', type: 'success' };
    }
  } catch (error) {
    return { title: 'Error', msg: `Ooops, an error occured while saving. ${error.message}`, type: 'error' };
  }
};

//===============================================================================================
// get the record using records API
const getRecord = async (recordID) => {
  if (recordID) {
    const record = await client.records.find(recordID);
    console.log(record);
    return record;
  }
  return null;
};

function useRecord(recordID) {
  return useQuery(['record', recordID], () => getRecord(recordID), {});
}
//===============================================================================================

/**
 * Attaches a given access token to a Microsoft Graph API call. Returns information about the user
 */
const getProfileData = async (accessToken) => {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);

  const options = {
    method: 'GET',
    headers
  };

  return fetch(graphConfig.graphMeEndpoint, options)
    .then((response) => response.json())
    .catch((error) => console.log(error));
};

function useProfileData(accessToken) {
  return useQuery(['profile', accessToken], () => getProfileData(accessToken), {});
}
//===============================================================================================
const getProfilePhoto = async (accessToken) => {
  const headers = new Headers();
  const bearer = `Bearer ${accessToken}`;

  headers.append('Authorization', bearer);

  const options = {
    method: 'GET',
    headers
  };

  return fetch(graphConfig.graphPhotoEndpoint, options)
    .then((response) => response.blob())
    .catch((error) => console.log(error));
};

function useProfilePhoto(accessToken) {
  return useQuery(['profilePhoto', accessToken], () => getProfilePhoto(accessToken), {});
}
//===============================================================================================

export {
  useApps, useProfileData, useProfilePhoto, useAudits, useObservations, getAudits, getApps, useRecord, updateObservations, getForm, useForm,
  useParentRecords, useChildRecords, useClassificationSet, useChoiceList, addObservation
};
export { getObservationsLayout, findObservationsRepeatableElement, OBSERVATIONS_KEY } from './formObservationLayout';
